import { useMutation, useQuery, useQueryClient } from 'react-query';
import { client } from 'src/utils/api-client';
import {
  LcaDiagramSchema,
  LcaFolderFilter,
  LcaGeneratedReportOutSchema,
  LcaProductAttachmentOutSchema,
  LcaProductBulkDeleteResult,
  LcaProductBulkMoveResult,
  LcaProductCertificateInSchema,
  LcaProductCertificateOutSchema,
  LcaProductFolderOutSchema,
  LcaProductFolderSummaryOutSchema,
  LcaProductKind,
  LcaProductOrdering,
  LcaNewProductExcelImportSuccess,
  LcaOrganizationSettings,
  LcaProductOutSchema,
  Pagination,
  ProductPassportData,
  LcaReportDraftOutSchema,
} from './types';
import { ProductImpactOutSchema } from 'src/Lca/LCADiagram/Diagram/index.types';
import { useAppSelector } from 'src/redux-file/hooks';
import {
  useImage,
  useLcaOrganization,
  useResourceController,
} from 'src/common/hooks';

// eslint-disable-next-line import/prefer-default-export
export const useLcaDiagram = (productId?: number) => {
  const query = useQuery(
    ['lca-diagram', { productId }],
    () =>
      client
        .get<LcaDiagramSchema>(`/web/lca/flowchart/${productId}`)
        .then((response) => response.data),
    { enabled: !!productId }
  );
  const stagesAsOptions = (query.data?.stages || []).map(({ key, name }) => ({
    value: key,
    label: name,
  }));

  return { stagesAsOptions, ...query };
};

export const useProductImpact = (
  productId: number,
  areaId: number | undefined
) => {
  const url = `/web/lca/products/${productId}/impact/${areaId}`;
  const query = useQuery(
    ['lca-diagram', 'calculations', { productId, areaId }],
    () =>
      client.get<ProductImpactOutSchema>(url).then((response) => response.data),
    { enabled: !!areaId }
  );
  return query;
};

export const useProductImpacts = (productId: number) => {
  const url = `/web/lca/products/${productId}/impact`;
  const query = useQuery(
    ['lca-diagram', 'calculations', { productId }],
    () =>
      client
        .get<ProductImpactOutSchema[]>(url)
        .then((response) => response.data),
    { enabled: !!productId }
  );
  return query;
};

export const useEcoinventProLicenceValid = () => {
  const isEcoinventLicenceValid = useAppSelector(
    (state) => state.platform.user?.ecoinventProLicenceIsValid
  );
  return { isEcoinventLicenceValid };
};

export const useLcaProduct = (id?: number) => {
  const url = `/web/lca/products/${id}`;
  const queryKey = ['lca', 'products', id];

  const query = useQuery(queryKey, () =>
    client.get<LcaProductOutSchema>(url).then((response) => response.data)
  );

  return {
    product: query.data,
    ...query,
  };
};

const lcaOrganizationSettingsKey = (organizationId?: number) => [
  'lca',
  'organization-settings',
  organizationId,
];

export const useLcaOrganizationSettings = (organizationId?: number) => {
  const query = useQuery(
    lcaOrganizationSettingsKey(organizationId),
    () =>
      client
        .get<LcaOrganizationSettings>(
          `/web/lca/organizations/${organizationId}/settings`
        )
        .then((response) => response.data),
    { enabled: !!organizationId }
  );

  return { settings: query.data, ...query };
};

export const useUpdateLcaOrganizationSettings = (organizationId: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    // only the version preselected for new products; existing products keep theirs
    mutationFn: (defaultEcoinventVersion: string) =>
      client
        .put<LcaOrganizationSettings>(
          `/web/lca/organizations/${organizationId}/settings`,
          { default_ecoinvent_version: defaultEcoinventVersion }
        )
        .then((response) => response.data),
    onSuccess: (settings) => {
      queryClient.setQueryData(
        lcaOrganizationSettingsKey(organizationId),
        settings
      );
    },
  });
};

interface UseLcaProductsOptions {
  organizationId: number;
  page: number;
  elementsPerPage: number;
  name?: string;
  // number → that folder, null → products without a folder, undefined → all
  folder?: LcaFolderFilter;
  kind?: LcaProductKind;
  ordering?: LcaProductOrdering;
}

export const useLcaProducts = ({
  organizationId,
  page,
  elementsPerPage,
  name,
  folder,
  kind = 'all',
  ordering = 'recently_updated',
}: UseLcaProductsOptions) => {
  const url = '/web/lca/products';
  const params = {
    organization_id: organizationId,
    offset: (page - 1) * elementsPerPage,
    limit: elementsPerPage,
    name,
    folder_id: typeof folder === 'number' ? folder : undefined,
    no_folder: folder === null ? true : undefined,
    kind,
    ordering,
  };
  const queryKey = ['lca', 'products', params];

  const query = useQuery(
    queryKey,
    () =>
      client
        .get<Pagination<LcaProductOutSchema>>(url, { params })
        .then((response) => response.data),
    { enabled: !!organizationId }
  );

  return {
    products: query.data?.items || [],
    count: query.data?.count,
    ...query,
  };
};

const FOLDERS_URL = '/web/lca/product-folders';

export const useLcaProductFolders = (organizationId: number) => {
  const params = { organization_id: organizationId };
  const query = useQuery(
    ['lca', 'product-folders', params],
    () =>
      client
        .get<LcaProductFolderOutSchema[]>(FOLDERS_URL, { params })
        .then((response) => response.data),
    { enabled: !!organizationId }
  );

  return { folders: query.data || [], ...query };
};

export const useLcaProductFoldersSummary = (organizationId: number) => {
  const params = { organization_id: organizationId };
  const query = useQuery(
    ['lca', 'product-folders', 'summary', params],
    () =>
      client
        .get<LcaProductFolderSummaryOutSchema>(`${FOLDERS_URL}/summary`, {
          params,
        })
        .then((response) => response.data),
    { enabled: !!organizationId }
  );

  return { summary: query.data, ...query };
};

export const useLcaProductFolderActions = (organizationId: number) => {
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['lca', 'product-folders'] });
    queryClient.invalidateQueries({ queryKey: ['lca', 'products'] });
  };

  const createFolder = useMutation({
    mutationFn: ({
      name,
      parentId,
    }: {
      name: string;
      parentId: number | null;
    }) =>
      client
        .post<LcaProductFolderOutSchema>(FOLDERS_URL, {
          organization_id: organizationId,
          name,
          parent_id: parentId,
        })
        .then((response) => response.data),
    onSuccess: invalidate,
  });

  const renameFolder = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) =>
      client
        .put<LcaProductFolderOutSchema>(`${FOLDERS_URL}/${id}`, { name })
        .then((response) => response.data),
    onSuccess: invalidate,
  });

  const moveFolder = useMutation({
    mutationFn: ({ id, parentId }: { id: number; parentId: number | null }) =>
      client
        .put<LcaProductFolderOutSchema>(`${FOLDERS_URL}/${id}`, {
          parent_id: parentId,
        })
        .then((response) => response.data),
    onSuccess: invalidate,
  });

  const deleteFolder = useMutation({
    mutationFn: (id: number) => client.delete(`${FOLDERS_URL}/${id}`),
    onSuccess: invalidate,
  });

  return { createFolder, renameFolder, moveFolder, deleteFolder };
};

export const useLcaProductBulkActions = () => {
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['lca', 'products'] });
    queryClient.invalidateQueries({ queryKey: ['lca', 'product-folders'] });
  };

  const moveProducts = useMutation({
    mutationFn: ({
      productIds,
      folderId,
    }: {
      productIds: number[];
      folderId: number | null;
    }) =>
      client
        .post<LcaProductBulkMoveResult>('/web/lca/products/bulk-move', {
          product_ids: productIds,
          folder_id: folderId,
        })
        .then((response) => response.data),
    onSuccess: invalidate,
  });

  const deleteProducts = useMutation({
    mutationFn: (productIds: number[]) =>
      client
        .post<LcaProductBulkDeleteResult>('/web/lca/products/bulk-delete', {
          product_ids: productIds,
        })
        .then((response) => response.data),
    onSuccess: invalidate,
  });

  return { moveProducts, deleteProducts };
};

export const useLcaImage = (imageId: string) => {
  const organization = useLcaOrganization();
  return useImage(organization.id, imageId);
};

export const useGeneratedReports = (productId?: number) => {
  const url = `/web/lca/products/${productId}/generated-reports`;
  const queryKey = ['lca', 'generated-reports', productId];

  const query = useQuery(
    queryKey,
    () =>
      client
        .get<LcaGeneratedReportOutSchema[]>(url)
        .then((response) => response.data),
    { enabled: !!productId }
  );

  return {
    generatedReports: query.data || [],
    ...query,
  };
};

export const useCreateGeneratedReport = (productId?: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (formData: FormData) =>
      client
        .post<LcaGeneratedReportOutSchema>(
          `/web/lca/products/${productId}/generated-reports`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
          }
        )
        .then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries(['lca', 'generated-reports', productId]);
    },
  });
};

export const useReportDraft = (productId?: number) => {
  const url = `/web/lca/products/${productId}/report-draft`;
  const queryKey = ['lca', 'report-draft', productId];

  const query = useQuery(
    queryKey,
    () =>
      client
        .get<LcaReportDraftOutSchema>(url)
        .then((response) => (response.status === 204 ? null : response.data)),
    { enabled: !!productId }
  );

  return {
    reportDraft: query.data || null,
    ...query,
  };
};

export const useUpsertReportDraft = (productId?: number) => {
  const queryClient = useQueryClient();
  const queryKey = ['lca', 'report-draft', productId];

  return useMutation({
    mutationFn: (payload: {
      config_snapshot: Record<string, unknown>;
      selected_area_id: number | null;
    }) =>
      client
        .put<LcaReportDraftOutSchema>(
          `/web/lca/products/${productId}/report-draft`,
          payload
        )
        .then((response) => response.data),
    onSuccess: (reportDraft) => {
      queryClient.setQueryData(queryKey, reportDraft);
    },
  });
};

export const useDeleteReportDraft = (productId?: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      client.delete(`/web/lca/products/${productId}/report-draft`),
    onSuccess: () => {
      queryClient.invalidateQueries(['lca', 'report-draft', productId]);
    },
  });
};

export const _useLcaProduct = () => {
  const queryClient = useQueryClient();

  const createFromExcel = useMutation({
    mutationFn: (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      return client.post('/web/lca/products/import-from-excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['lca', 'products']);
    },
  });

  const createNewFromExcel = useMutation({
    mutationFn: ({
      file,
      organizationId,
      ecoinventVersion,
    }: {
      file: File;
      organizationId: number;
      ecoinventVersion: string;
    }) => {
      const formData = new FormData();
      formData.append('file', file);
      return client.post<LcaNewProductExcelImportSuccess>(
        '/web/lca/products/import-new-from-excel',
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          params: {
            organization_id: organizationId,
            ecoinvent_version: ecoinventVersion,
          },
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['lca', 'products']);
    },
  });

  const downloadNewProductTemplate = async () => {
    const response = await client.get(
      '/web/lca/products/new-product-template',
      {
        responseType: 'blob',
      }
    );

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'lca_new_product_template.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return { createFromExcel, createNewFromExcel, downloadNewProductTemplate };
};

export const useLcaAttachments = (
  attachmentType: string,
  queryParams: { product_id: number }
) =>
  useResourceController<
    LcaProductAttachmentOutSchema,
    LcaProductAttachmentOutSchema
  >(
    `/web/lca/${attachmentType}`,
    ['lca', attachmentType, queryParams],
    queryParams,
    undefined,
    ['GET', 'DELETE']
  );

export const useLcaProductCertificates = (queryParams: {
  product_id: number;
}) =>
  useResourceController<
    LcaProductCertificateInSchema,
    LcaProductCertificateOutSchema
  >(
    `/web/lca/certificates`,
    ['lca', 'certificates', queryParams],
    queryParams,
    undefined
  );

export const useProductPassport = (passportToken: string) => {
  const url = `/web/lca/products/passport-token/${passportToken}`;
  const queryKey = ['lca', 'passport', passportToken];

  const query = useQuery(
    queryKey,
    () =>
      client.get<ProductPassportData>(url).then((response) => response.data),
    {
      enabled: !!passportToken,
    }
  );

  return {
    product: query.data,
    ...query,
  };
};

export const useProductPassportImpacts = (
  passportToken: string,
  enabled = true
) => {
  const url = `/web/lca/products/passport-token/${passportToken}`;
  const queryKey = ['lca', 'passport', passportToken, 'impacts'];

  const query = useQuery(
    queryKey,
    () =>
      client
        .get<ProductPassportData>(url, { params: { include_impacts: true } })
        .then((response) => response.data.impacts),
    {
      enabled: !!passportToken && enabled,
    }
  );

  return {
    impacts: query.data,
    ...query,
  };
};
